import { createContext, useContext, useState, useEffect } from 'react'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }) => {
  const [isNight, setIsNight] = useState(() => {
    const stored = localStorage.getItem('vigilantedge-mode')
    return stored === 'night'
  })

  useEffect(() => {
    // Persist to localStorage
    localStorage.setItem('vigilantedge-mode', isNight ? 'night' : 'day')

    const root = document.documentElement

    // Toggle Tailwind dark mode class
    if (isNight) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Set CSS variables for fonts and effects
    if (isNight) {
      // Night mode - Cyberpunk fonts
      root.style.setProperty('--font-display', 'Orbitron, sans-serif')
      root.style.setProperty('--font-body', '"IBM Plex Sans", sans-serif')
      // Neon glow effects
      root.style.setProperty('--neon-cyan-glow', '0 0 10px rgba(6, 182, 212, 0.8), 0 0 20px rgba(6, 182, 212, 0.6), 0 0 40px rgba(6, 182, 212, 0.4)')
      root.style.setProperty('--neon-magenta-glow', '0 0 10px rgba(236, 72, 153, 0.8), 0 0 20px rgba(236, 72, 153, 0.6)')
      root.style.setProperty('--logo-glow', 'drop-shadow(0 0 8px rgba(6, 182, 212, 0.6)) drop-shadow(0 0 16px rgba(236, 72, 153, 0.4))')
      root.style.setProperty('--logo-glow-hover', 'drop-shadow(0 0 12px rgba(6, 182, 212, 0.8)) drop-shadow(0 0 24px rgba(236, 72, 153, 0.6))')
    } else {
      // Day mode - System/professional fonts
      root.style.setProperty('--font-display', 'system-ui, -apple-system, sans-serif')
      root.style.setProperty('--font-body', 'system-ui, -apple-system, sans-serif')
      // Clear glow effects
      root.style.setProperty('--neon-cyan-glow', 'none')
      root.style.setProperty('--neon-magenta-glow', 'none')
      root.style.setProperty('--logo-glow', 'none')
      root.style.setProperty('--logo-glow-hover', 'none')
    }

    // Set data attribute for CSS selectors
    root.setAttribute('data-mode', isNight ? 'night' : 'day')
  }, [isNight])

  const toggleTheme = () => setIsNight((prev) => !prev)
  const setDayMode = () => setIsNight(false)
  const setNightMode = () => setIsNight(true)

  const value = {
    isNight,
    toggleTheme,
    setDayMode,
    setNightMode,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
